import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/axiosConfig";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import img from "../STC_logo-01.png";
import loginBg from "../assets/login-bg.png";
import "../css/login.css";
import "../css/forgotPassword.css";

const API_URL = process.env.REACT_APP_API_URL;

// Must match OTP_LENGTH in routes/auth.js
const OTP_LENGTH = 6;

const ForgotPassword = () => {
  const [step, setStep] = useState(1); // 1 email, 2 code, 3 new password
  const [email, setEmail] = useState("");
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(""));
  const [resetToken, setResetToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [shake, setShake] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const boxRefs = useRef([]);
  const navigate = useNavigate();

  // Resend countdown
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Focus the first box when the code step opens
  useEffect(() => {
    if (step === 2 && boxRefs.current[0]) boxRefs.current[0].focus();
  }, [step]);

  const failWithShake = (message) => {
    setError(message);
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  const sendCode = async (isResend) => {
    setError("");
    setNotice("");
    if (!email.trim()) {
      setError("Please enter your registered email.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`${API_URL}/auth/forgot-password`, {
        email: email.trim().toLowerCase(),
      });
      setNotice(
        (data && data.message) ||
          "If an account exists for that email, a code has been sent."
      );
      setDigits(Array(OTP_LENGTH).fill(""));
      setStep(2);
      setCooldown(60);
      if (isResend && boxRefs.current[0]) boxRefs.current[0].focus();
    } catch (err) {
      const res = err && err.response;
      if (res && res.status === 429 && res.data && res.data.retryAfter) {
        setCooldown(res.data.retryAfter);
      }
      setError(
        (res && res.data && res.data.message) ||
          "Could not send the code. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (code) => {
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post(`${API_URL}/auth/verify-otp`, {
        email: email.trim().toLowerCase(),
        otp: code,
      });
      setResetToken((data && data.resetToken) || "");
      setNotice("Code verified. Choose a new password.");
      setStep(3);
    } catch (err) {
      const msg =
        (err && err.response && err.response.data && err.response.data.message) ||
        "Incorrect code. Please try again.";
      failWithShake(msg);
      setDigits(Array(OTP_LENGTH).fill(""));
      if (boxRefs.current[0]) boxRefs.current[0].focus();
    } finally {
      setBusy(false);
    }
  };

  const handleDigit = (index, value) => {
    const clean = value.replace(/\D/g, "");
    if (!clean) {
      const next = [...digits];
      next[index] = "";
      setDigits(next);
      return;
    }
    const next = [...digits];
    // Typing or pasting several characters fills forward from here.
    clean.split("").forEach((ch, k) => {
      if (index + k < OTP_LENGTH) next[index + k] = ch;
    });
    setDigits(next);

    const landed = Math.min(index + clean.length, OTP_LENGTH - 1);
    if (boxRefs.current[landed]) boxRefs.current[landed].focus();

    // NB: "123456".includes("") is true for every string, so test the array.
    if (next.every((ch) => ch !== "")) {
      verifyCode(next.join(""));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (digits.every((ch) => ch !== "") && !busy) verifyCode(digits.join(""));
      return;
    }
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      const prev = boxRefs.current[index - 1];
      if (prev) prev.focus();
    }
    if (e.key === "ArrowLeft" && index > 0 && boxRefs.current[index - 1]) {
      boxRefs.current[index - 1].focus();
    }
    if (
      e.key === "ArrowRight" &&
      index < OTP_LENGTH - 1 &&
      boxRefs.current[index + 1]
    ) {
      boxRefs.current[index + 1].focus();
    }
  };

  const handlePaste = (e) => {
    const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "");
    if (!text) return;
    e.preventDefault();
    const next = Array(OTP_LENGTH).fill("");
    text.split("").forEach((ch, k) => {
      if (k < OTP_LENGTH) next[k] = ch;
    });
    setDigits(next);
    const last = Math.min(text.length, OTP_LENGTH) - 1;
    if (boxRefs.current[last]) boxRefs.current[last].focus();
    if (text.length >= OTP_LENGTH) verifyCode(next.join(""));
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("The two passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`${API_URL}/auth/reset-password`, {
        email: email.trim().toLowerCase(),
        resetToken,
        newPassword: password,
      });
      setStep(4);
      setTimeout(() => navigate("/"), 2200);
    } catch (err) {
      setError(
        (err && err.response && err.response.data && err.response.data.message) ||
          "Could not reset the password. Please start again."
      );
    } finally {
      setBusy(false);
    }
  };

  const strength = (() => {
    let score = 0;
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score += 1;
    if (/\d/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;
    return Math.min(score, 4);
  })();
  const strengthLabels = ["Too weak", "Weak", "Fair", "Good", "Strong"];

  return (
    <div className="login-page" style={{ backgroundImage: `url(${loginBg})` }}>
      <div className="login-overlay" />
      <div className="login-card fp-card">
        <img src={img} className="img" alt="logo" />

        <div className="fp-steps" aria-hidden="true">
          {[1, 2, 3].map((n) => (
            <span
              key={n}
              className={`fp-step-dot${step >= n ? " done" : ""}`}
            />
          ))}
        </div>

        {step === 1 && (
          <div className="fp-pane">
            <h1>Forgot Password</h1>
            <p className="login-sub">
              Enter your registered email and we will send you a verification
              code.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                sendCode(false);
              }}
            >
              <input
                type="email"
                placeholder="Registered email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{ width: "100%" }}
              />
              {error && <p className="error">{error}</p>}
              <div className="bttn">
                <button type="submit" className="button" disabled={busy}>
                  {busy ? "Sending…" : "Send Code"}
                </button>
              </div>
            </form>
            <button
              type="button"
              className="button button-ghost"
              onClick={() => navigate("/")}
            >
              Back to Sign In
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="fp-pane">
            <div className="fp-badge">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none"
                   stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                   strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <h1>Verification Code</h1>
            <p className="login-sub">
              Enter the {OTP_LENGTH}-digit code sent to
              <br />
              <strong className="fp-email">{email}</strong>
            </p>

            <div className={`fp-otp${shake ? " shake" : ""}`} onPaste={handlePaste}>
              {digits.map((d, i) => (
                <div key={i} className={`fp-box${d ? " filled" : ""}`}>
                  <span className="fp-liquid" />
                  <input
                    ref={(el) => {
                      boxRefs.current[i] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={OTP_LENGTH}
                    value={d}
                    disabled={busy}
                    onChange={(e) => handleDigit(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    onFocus={(e) => e.target.select()}
                    aria-label={`Digit ${i + 1}`}
                  />
                </div>
              ))}
            </div>

            {busy && <p className="fp-verifying">Verifying…</p>}
            {error && <p className="error">{error}</p>}
            {!error && notice && <p className="fp-notice">{notice}</p>}

            <div className="bttn">
              <button
                type="button"
                className="button"
                disabled={busy || digits.some((ch) => ch === "")}
                onClick={() => verifyCode(digits.join(""))}
              >
                {busy ? "Verifying…" : "Verify Code"}
              </button>
            </div>

            <p className="fp-resend">
              Didn&apos;t receive the code?{" "}
              {cooldown > 0 ? (
                <span className="fp-resend-wait">Resend in {cooldown}s</span>
              ) : (
                <button
                  type="button"
                  className="fp-link"
                  onClick={() => sendCode(true)}
                  disabled={busy}
                >
                  Resend OTP
                </button>
              )}
            </p>

            <button
              type="button"
              className="button button-ghost"
              onClick={() => {
                setStep(1);
                setError("");
                setNotice("");
              }}
            >
              Change Email
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="fp-pane">
            <h1>New Password</h1>
            <p className="login-sub">Choose a strong password you will remember.</p>
            <form onSubmit={submitPassword}>
              <div className="password-input" style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="New password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={{ width: "100%" }}
                />
                <div
                  className="eye-icon"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                  }}
                >
                  <FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} />
                </div>
              </div>

              {password && (
                <div className="fp-strength">
                  <div className={`fp-strength-bar level-${strength}`} />
                  <span>{strengthLabels[strength]}</span>
                </div>
              )}

              <input
                type={showPassword ? "text" : "password"}
                placeholder="Confirm new password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                style={{ width: "100%" }}
              />
              {error && <p className="error">{error}</p>}
              <div className="bttn">
                <button type="submit" className="button" disabled={busy}>
                  {busy ? "Updating…" : "Reset Password"}
                </button>
              </div>
            </form>
          </div>
        )}

        {step === 4 && (
          <div className="fp-pane fp-success">
            <div className="fp-tick">
              <svg viewBox="0 0 52 52" width="64" height="64">
                <circle
                  className="fp-tick-circle"
                  cx="26"
                  cy="26"
                  r="24"
                  fill="none"
                />
                <path
                  className="fp-tick-check"
                  fill="none"
                  d="M14 27l8 8 16-16"
                />
              </svg>
            </div>
            <h1>Password Updated</h1>
            <p className="login-sub">Taking you back to sign in…</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
